import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import audioData from '../assets/audio/index';
import playButton from '../assets/images/play-button.png';
import soundwaves from '../assets/images/soundwaves.png';
import Modal from '../components/modal';
import { useTestState } from '../context/TestContext';
import useTracking from '../hooks/useTracking';

/** Max wrong-ear selections per ear before the test is blocked. */
const MAX_FAILURES_PER_EAR = 2;

interface HeadphoneCheckResult {
  ear: string;
  correct: boolean;
  attempts: number;
  failures: number;
}

const Headphones = ({ isB2b }: { isB2b?: boolean }) => {
  const { t } = useTranslation('headphones');
  const [playing, setPlaying] = useState(false);
  const [modal, setModal] = useState(false);
  const [hardStopModal, setHardStopModal] = useState(false);
  const [testSoundPlayed, setTestSoundPlayed] = useState(false);
  const [isPlayingSound, setIsPlayingSound] = useState(false);
  const [showEarSelection, setShowEarSelection] = useState(false);
  const [selectedEar, setSelectedEar] = useState('');

  const [currentTestEar, setCurrentTestEar] = useState('');
  const [testResults, setTestResults] = useState<HeadphoneCheckResult[]>([]);
  const [currentTestAttempt, setCurrentTestAttempt] = useState(0);
  const [testPhase, setTestPhase] = useState<'first' | 'second' | 'complete'>(
    'first',
  );
  const [earFailures, setEarFailures] = useState<{
    left: number;
    right: number;
  }>({ left: 0, right: 0 });
  const [blocked, setBlocked] = useState(false);

  const [tracked, setTracked] = useState(false);
  const { trackEvent } = useTracking();

  const { state, dispatch } = useTestState();
  const navigate = useNavigate();

  useEffect(() => {
    if (!state.contactid) {
      return navigate('/demographics');
    }
  }, [navigate, state.contactid]);

  useEffect(() => {
    if (!tracked && state.email) {
      trackEvent('JHT_CHECK_HEADPHONES_PAGE_LOAD');
      setTracked(true);
    }
  }, [state.email, trackEvent, tracked]);

  useEffect(() => {
    // DEBUGGING chunk: useful for navigating directly to /test and omit redirect
    // use a test contact id
    // dispatch({ type: 'UPDATE_CONTACTID', payload: '003Ud00000DRiBx' });

    const ears = ['left', 'right'];
    const randomEar = ears[Math.floor(Math.random() * ears.length)];
    setCurrentTestEar(randomEar);

    dispatch({ type: 'UPDATE_PROGRESS', payload: 80 });
  }, [dispatch]);

  const logHeadphoneCheckResult = (result: HeadphoneCheckResult) => {
    const existingNotes = state.endoftestnotes || '';
    const outcome = result.correct
      ? 'correct'
      : 'incorrect (wrong ear selected)';
    const headphoneCheckNote = `Headphone check - ${result.ear} ear: ${outcome} (attempt ${result.attempts}, ${result.failures} failures)`;
    const newNotes = existingNotes
      ? `${existingNotes}; ${headphoneCheckNote}`
      : headphoneCheckNote;

    dispatch({ type: 'UPDATE_ENDOFTESTNOTES', payload: newNotes });
  };

  const resetAttemptUi = () => {
    setTestSoundPlayed(false);
    setSelectedEar('');
    setShowEarSelection(false);
    setPlaying(false);
    setIsPlayingSound(false);
  };

  const triggerHardStop = () => {
    setBlocked(true);
    setHardStopModal(true);
    dispatch({ type: 'UPDATE_HEADPHONES_CHECK_PASSED', payload: false });
    resetAttemptUi();
  };

  const handleReturnHome = () => {
    setHardStopModal(false);
    navigate('/');
  };

  const handlePlaySound = () => {
    if (blocked) return;
    setIsPlayingSound(true);
    setTestSoundPlayed(true);
    setPlaying(true);
    setShowEarSelection(false);
    setSelectedEar('');
  };

  const handleEarSelection = (selectedSide: 'left' | 'right') => {
    if (blocked) return;

    const isCorrect = selectedSide === currentTestEar;
    const newAttempt = currentTestAttempt + 1;
    const priorFailures =
      earFailures[currentTestEar as keyof typeof earFailures];
    const nextFailures = isCorrect ? priorFailures : priorFailures + 1;

    const result: HeadphoneCheckResult = {
      ear: currentTestEar,
      correct: isCorrect,
      attempts: newAttempt,
      failures: nextFailures,
    };

    setTestResults([...testResults, result]);
    setCurrentTestAttempt(newAttempt);

    if (!isCorrect) {
      setEarFailures((prev) => ({
        ...prev,
        [currentTestEar]: nextFailures,
      }));
    }

    logHeadphoneCheckResult(result);

    if (isCorrect) {
      if (testPhase === 'first') {
        const nextEar = currentTestEar === 'left' ? 'right' : 'left';
        setCurrentTestEar(nextEar);
        setTestPhase('second');
        setCurrentTestAttempt(0);
        resetAttemptUi();
      } else {
        setTestPhase('complete');
        dispatch({ type: 'UPDATE_HEADPHONES_CHECK_PASSED', payload: true });
      }
      return;
    }

    // Wrong ear: hard-stop after MAX_FAILURES_PER_EAR; otherwise allow retry.
    if (nextFailures >= MAX_FAILURES_PER_EAR) {
      triggerHardStop();
    } else {
      setModal(true);
    }
  };

  const handleLeftEarClick = () => {
    if (!testSoundPlayed || blocked) return;
    setSelectedEar('left');
    handleEarSelection('left');
  };

  const handleRightEarClick = () => {
    if (!testSoundPlayed || blocked) return;
    setSelectedEar('right');
    handleEarSelection('right');
  };

  const handleModalClose = () => {
    setModal(false);
    resetAttemptUi();
  };

  const getTitleText = () => {
    if (testPhase === 'first') {
      return t('top-title');
    } else if (testPhase === 'second') {
      return t('title-second-ear');
    } else {
      return t('title-complete');
    }
  };

  const getInstructionText = () => {
    if (testPhase === 'complete') {
      return t('completion-message');
    }
    return t('which-ear-question');
  };

  const getModalText = () => {
    const oppositeEar = currentTestEar === 'left' ? 'right' : 'left';
    return t('modal-text-incorrect', {
      currentEar: currentTestEar,
      oppositeEar: oppositeEar,
    });
  };

  return (
    <div className="headphones">
      {modal && (
        <Modal closeModal={handleModalClose}>
          <div style={{ padding: '40px 10px' }}>
            <h2
              style={{
                fontSize: 24,
                fontFamily: 'Georgia',
                textAlign: 'center',
                marginBottom: '20px',
                fontWeight: 'normal',
              }}
            >
              {t('retry-modal-title')}
            </h2>
            <p
              style={{
                fontSize: 20,
                fontFamily: 'Georgia',
                textAlign: 'center',
                lineHeight: '25px',
              }}
            >
              {getModalText()}
            </p>
            <button
              className="link-button"
              onClick={handleModalClose}
              style={{ marginTop: '20px' }}
            >
              {t('modal-button')}
            </button>
          </div>
        </Modal>
      )}

      {hardStopModal && (
        <Modal closeModal={handleReturnHome}>
          <div style={{ padding: '40px 10px' }}>
            <h2
              style={{
                fontSize: 24,
                fontFamily: 'Georgia',
                textAlign: 'center',
                marginBottom: '20px',
                fontWeight: 'normal',
              }}
            >
              {isB2b ? t('proctor-modal-title') : t('hard-stop-title')}
            </h2>
            <p
              style={{
                fontSize: 20,
                fontFamily: 'Georgia',
                textAlign: 'center',
                lineHeight: '25px',
              }}
            >
              {isB2b ? t('proctor-modal-message') : t('hard-stop-message')}
            </p>
            <button
              className="link-button"
              onClick={handleReturnHome}
              style={{ marginTop: '20px' }}
            >
              {isB2b ? t('proctor-modal-button') : t('hard-stop-button')}
            </button>
          </div>
        </Modal>
      )}

      <h1 className="top-title-text">{getTitleText()}</h1>

      {testPhase !== 'complete' && (
        <p
          className="instructions"
          style={{
            fontSize: 18,
            fontFamily: 'Georgia',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          {t('instructions')}
        </p>
      )}

      {testPhase !== 'complete' && (
        <div className="main-content">
          <img
            className="soundwaves"
            src={soundwaves}
            alt={t('sound-waves-alt')}
          />
          {playing ? (
            <audio
              src={
                audioData[
                  `${currentTestEar === 'left' ? 'L' : 'R'}_arpeggio.wav`
                ]
              }
              autoPlay
              onEnded={() => {
                setPlaying(false);
                setIsPlayingSound(false);
                setShowEarSelection(true);
              }}
            />
          ) : (
            <button
              className="play-button"
              onClick={handlePlaySound}
              disabled={isPlayingSound || blocked}
            >
              <img src={playButton} alt={t('play-button-alt')} />
            </button>
          )}
        </div>
      )}

      {testPhase !== 'complete' && showEarSelection && (
        <>
          <p className="ear-question">{getInstructionText()}</p>

          <div className="left-right-buttons">
            <button
              type="button"
              className={`response-button ${selectedEar === 'left' ? 'selected' : ''}`}
              onClick={handleLeftEarClick}
              disabled={!testSoundPlayed || isPlayingSound || blocked}
            >
              {t('left')}
            </button>
            <button
              type="button"
              className={`response-button ${selectedEar === 'right' ? 'selected' : ''}`}
              onClick={handleRightEarClick}
              disabled={!testSoundPlayed || isPlayingSound || blocked}
            >
              {t('right')}
            </button>
          </div>
        </>
      )}

      {testPhase === 'complete' && !blocked && (
        <>
          <p className="completion-message">{getInstructionText()}</p>
          <Link className="link-button start-test-button" to="/instructions">
            {t('start-test')}
          </Link>
        </>
      )}

      <p className="volume-warning">
        {testPhase !== 'complete' ? t('do-not-adjust-volume') : ''}
      </p>
    </div>
  );
};

export default Headphones;
